/**
 * 成员与会籍列表。
 *
 * 敏感列（健康 / 财富）的渲染规则是整个后台最关键的一条：
 *   服务端按权限裁剪字段（core/rbac.ts 的 redactMemberFields），
 *   无权时字段**根本不下发**。因此这里：
 *     · 有值的行 → 显示真实数值；
 *     · 整列都没有该字段（例如财务看不到健康）→ 整列显示「无权限查看」；
 *     · 个别行缺失 → 该行显示「无权限查看」，**绝不用 0 或空白冒充**。
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../lib/api.ts';
import type { MemberItem } from '../lib/api.ts';
import { useLoad } from '../lib/useLoad.ts';
import { Card, NoPermission, PageHead } from '../components/ui.tsx';
import { EmptyBox, ErrorBox, LoadingBox, errorText } from '../components/StateBox.tsx';
import { EMPTY_TEXT, formatText, levelLabel, scopeLabel } from '../lib/format.ts';

/** 健康列对应的字段：任一存在即代表「有权限」，可以渲染整列。 */
const HEALTH_KEYS = ['health_score', 'health_grade', 'bio_age', 'organ_age'] as const;
/** 财富列对应的字段。 */
const WEALTH_KEYS = ['wealth_score', 'wealth_grade', 'trust_plan'] as const;

function hasAny(item: MemberItem, keys: readonly string[]): boolean {
  const bag: Record<string, unknown> = { ...item };
  return keys.some((key) => bag[key] !== undefined);
}

/** 敏感数值单元：有权限显示数值，无权限显示「无权限查看」。 */
function SensitiveCell({ value }: { value: number | undefined }) {
  if (value === undefined) return <NoPermission />;
  return <span className="mono-num">{value}</span>;
}

export function MembersPage({ permissions }: { permissions: string[] }) {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState('');
  const [debounced, setDebounced] = useState('');

  // 关键词防抖：后台表格输入即查会让后端被无意义的中间态请求打满
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(keyword.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [keyword]);

  const { data, error, loading, reload } = useLoad((signal) => adminApi.members(debounced, 50, signal));

  if (loading) return <LoadingBox text="正在加载成员列表…" />;
  if (error) return <ErrorBox message={errorText(error)} onRetry={reload} />;
  if (!data) return <ErrorBox message="成员列表为空" onRetry={reload} />;

  const items = data.items;
  // 整列是否有权限：只要有一条记录带该字段，就说明当前角色有权限，可以渲染真实数据
  const healthVisible = items.some((item) => hasAny(item, HEALTH_KEYS));
  const wealthVisible = items.some((item) => hasAny(item, WEALTH_KEYS));
  // 建档入口按权限点显示：没有 member:write 的角色（管家 / 专家 / 财务 / 数据运营）看不到按钮。
  // **隐藏入口不等于有权限**：服务端才是裁决者，直接访问 /members/new 仍会被 403 拦下并原文提示。
  const canCreate = permissions.includes('member:write');

  return (
    <div>
      <PageHead
        title="成员与会籍"
        sub="可见范围由守护关系决定；健康与财富字段由独立权限点决定是否下发"
        actions={
          <>
            <span className="badge ghost">可见范围：{scopeLabel(data.scope)}</span>
            {canCreate && (
              <button type="button" className="btn primary sm" onClick={() => navigate('/members/new')}>
                新建成员建档
              </button>
            )}
          </>
        }
      />

      <Card tight>
        <div className="row">
          <input
            className="field-input"
            style={{ maxWidth: 320 }}
            placeholder="按姓名或成员编号搜索"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
          <span className="text-tiny muted">
            当前可见 {items.length} 名成员{debounced ? `（关键词：${debounced}）` : ''}
          </span>
        </div>
      </Card>

      <Card tight>
        {items.length === 0 && (
          <EmptyBox
            title="可见范围内没有成员"
            desc="若您的角色可见范围为「仅守护关系」，请确认是否已被分派到成员守护小组。"
          />
        )}

        {items.length > 0 && (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>编号</th>
                  <th>姓名</th>
                  <th>会籍</th>
                  <th>城市</th>
                  <th style={{ textAlign: 'right' }}>企业指数</th>
                  <th style={{ textAlign: 'right' }}>未解除预警</th>
                  <th>
                    健康（H）
                    {healthVisible ? (
                      <span className="badge h" style={{ marginLeft: 6 }}>
                        已授权
                      </span>
                    ) : (
                      <span className="badge ghost" style={{ marginLeft: 6 }}>
                        无权限
                      </span>
                    )}
                  </th>
                  <th>
                    财富（W）
                    {wealthVisible ? (
                      <span className="badge w" style={{ marginLeft: 6 }}>
                        已授权
                      </span>
                    ) : (
                      <span className="badge ghost" style={{ marginLeft: 6 }}>
                        无权限
                      </span>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td className="mono-num">{item.no}</td>
                    <td>
                      <button
                        type="button"
                        className="link"
                        onClick={() => navigate(`/members/${encodeURIComponent(item.id)}`)}
                      >
                        {item.name}
                      </button>
                    </td>
                    <td>{levelLabel(item.level)}</td>
                    <td>{formatText(item.city)}</td>
                    <td className="num-cell">{item.enterpriseScore === null ? EMPTY_TEXT : item.enterpriseScore}</td>
                    <td className="num-cell">
                      {item.openAlerts === undefined ? <NoPermission /> : item.openAlerts}
                    </td>
                    <td>
                      {!healthVisible ? (
                        <NoPermission what="健康分" />
                      ) : (
                        <>
                          <SensitiveCell value={item.health_score} />
                          {item.health_grade !== undefined && (
                            <span className="badge h" style={{ marginLeft: 6 }}>
                              {item.health_grade}
                            </span>
                          )}
                        </>
                      )}
                    </td>
                    <td>
                      {!wealthVisible ? (
                        <NoPermission what="财富健康分" />
                      ) : (
                        <>
                          <SensitiveCell value={item.wealth_score} />
                          {item.wealth_grade !== undefined && (
                            <span className="badge w" style={{ marginLeft: 6 }}>
                              {item.wealth_grade}
                            </span>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="note-src">
        <b>关于「无权限查看」</b>
        <br />
        健康（H 分、生物年龄、器官年龄）与财富（W 分、信托方案）是敏感个人信息，
        服务端按权限点 <span className="mono-num">member:read_health</span> /{' '}
        <span className="mono-num">member:read_wealth</span> 裁剪后下发：
        <b>无权时字段不存在</b>，前端因此显示「无权限查看」——不是 0，也不是空白。
        运营看不到健康与财富明细，财务看不到健康数据，这是职责分离的要求。
      </div>

      <div className="sp12" />
      {!healthVisible && (
        <div className="alert-chip warn">
          <span>⚠</span>
          <span>
            <b>当前角色看不到健康列</b>
            <br />
            这与「成员健康分为 0」是两件事。需要健康数据请联系超级管理员调整角色或被分派到健康板块守护小组。
          </span>
        </div>
      )}
      {!wealthVisible && (
        <div className="alert-chip warn">
          <span>⚠</span>
          <span>
            <b>当前角色看不到财富列</b>
            <br />
            财富明细仅向具备 <span className="mono-num">member:read_wealth</span> 权限的角色下发。
          </span>
        </div>
      )}
      <div className="sp8" />
    </div>
  );
}
